import { useEffect, useState } from "react";
import type { IncomingCall, IncomingOutcome } from "./SoftphoneProvider";

const COUNTDOWN_TICK_MS = 200;
const CLOSES_SOON = "Closes in 3 seconds";

export const SEND_TO_AI_TOOLTIP =
  "The AI agent answers now. Everyone else’s popup closes.";

export function useTeamFirstCountdown(incoming: IncomingCall | null) {
  const total = incoming?.teamFirstSeconds;
  const receivedAt = incoming?.receivedAt;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!total) return;
    setNow(Date.now());
    const handle = window.setInterval(
      () => setNow(Date.now()),
      COUNTDOWN_TICK_MS
    );
    return () => window.clearInterval(handle);
  }, [total, receivedAt]);

  if (!total || receivedAt === undefined) return null;
  const remaining = Math.max(0, total - (now - receivedAt) / 1000);
  return {
    total,
    remaining,
    secondsLeft: Math.max(1, Math.ceil(remaining))
  };
}

export interface IncomingOutcomeCopy {
  tone: "ai" | "warn";
  message: string;
  detail: string;
}

export function describeIncomingOutcome(
  outcome: IncomingOutcome | null
): IncomingOutcomeCopy | null {
  switch (outcome?.kind) {
    case "sentMe":
      return {
        tone: "ai",
        message: "Sent to the AI agent",
        detail: CLOSES_SOON
      };
    case "sentOther":
      return {
        tone: "ai",
        message: `${outcome.byName || "A teammate"} sent this call to the AI agent`,
        detail: CLOSES_SOON
      };
    case "timeout":
      return {
        tone: "ai",
        message: "No one answered, so the AI agent took the call",
        detail: CLOSES_SOON
      };
    case "raceAI":
      return {
        tone: "warn",
        message: "The AI agent already took this call",
        detail: "It’s in Inbox as an AI call. Closes in 3 seconds"
      };
    case "raceHuman":
      return {
        tone: "warn",
        message: `${outcome.byName || "A teammate"} already answered this call`,
        detail: CLOSES_SOON
      };
    default:
      return null;
  }
}
